import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import PdfPreview from "@/components/PdfPreview";
import { CheckCircle2, Download, XCircle, FileWarning } from "lucide-react";
import { classifyPreviewKind, normalizeUploadStatus } from "@/lib/uploadPreview";
import api, { formatApiError, formatBlobApiError } from "@/lib/api";
import { toast } from "sonner";

const REJECT_REASON_MAX = 500;

/**
 * FilePreviewModal — Phase DP shared preview for submission uploads.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - file: upload object from submission.uploads[] — { id, field_key,
 *      filename, mime_type, status, reason, ... }. May be null when the
 *      modal is closed (we tolerate `open=false` regardless).
 *  - submissionId: parent submission id (for the default download URL
 *      built as ``/submissions/{submissionId}/uploads/{file.id}``).
 *      Ignored when ``downloadUrl`` is provided.
 *  - downloadUrl: optional override URL for the GET that streams the
 *      file. Used by the admin Customer Detail page (Phase O.2) whose
 *      uploads live under a different endpoint
 *      (``/admin/customers/{id}/uploads/{upload_id}``) than the
 *      submission ones. When this is set, ``submissionId`` is not
 *      required.
 *  - mode: "admin" → renders Approve / Reject controls.
 *          "customer" → preview + download + close only.
 *  - onDecision: optional callback fired after a successful approve /
 *      reject; the parent uses it to refresh the upload row's status.
 */
export function FilePreviewModal({
    open,
    onOpenChange,
    file,
    submissionId,
    downloadUrl: downloadUrlOverride,
    mode = "admin",
    onDecision,
}) {
    const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [busy, setBusy] = useState(false);

    const kind = useMemo(() => classifyPreviewKind(file), [file]);
    const status = normalizeUploadStatus(file?.status);
    // Phase O.2 — callers may override the default submission-scoped
    // download URL with a sibling endpoint (e.g. the admin Customer
    // Detail page's ``/admin/customers/{id}/uploads/{upload_id}``).
    const downloadUrl = downloadUrlOverride
        || (file && submissionId
            ? `/submissions/${submissionId}/uploads/${file.id}`
            : null);

    // Fetch the file as a blob (auth header lives on the api client) and
    // create an object URL so PdfPreview / <img> can render it inline.
    useEffect(() => {
        if (!open || !file || !downloadUrl) {
            setPreviewBlobUrl(null);
            setPreviewError(null);
            return;
        }
        if (kind === "unsupported") {
            setPreviewBlobUrl(null);
            setPreviewError(null);
            return;
        }
        let cancelled = false;
        let createdUrl = null;
        setPreviewLoading(true);
        setPreviewError(null);
        (async () => {
            try {
                const resp = await api.get(downloadUrl, { responseType: "blob" });
                if (cancelled) return;
                createdUrl = URL.createObjectURL(resp.data);
                setPreviewBlobUrl(createdUrl);
            } catch (err) {
                if (!cancelled) setPreviewError(await formatBlobApiError(err));
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [open, file?.id, downloadUrl, kind]);

    // Reset reject pane on close.
    useEffect(() => {
        if (!open) {
            setRejecting(false);
            setRejectReason("");
        }
    }, [open]);

    const doDownload = async () => {
        if (!file || !downloadUrl) return;
        try {
            const resp = await api.get(downloadUrl, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.filename || "document";
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(await formatBlobApiError(err));
        }
    };

    const doApprove = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const { data } = await api.post(
                `/admin/submissions/${submissionId}/uploads/${file.id}/approve`
            );
            toast.success("Upload approved");
            onDecision?.(data);
            onOpenChange?.(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const doReject = async () => {
        if (!file) return;
        const reason = rejectReason.trim();
        if (!reason) return;
        setBusy(true);
        try {
            const { data } = await api.post(
                `/admin/submissions/${submissionId}/uploads/${file.id}/reject`,
                { reason }
            );
            toast.success("Upload rejected");
            onDecision?.(data);
            onOpenChange?.(false);
        } catch (err) {
            // Surface server-side field error inline when present.
            const fe = err?.response?.data?.detail?.field_errors?.reason;
            toast.error(fe || formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const renderPreviewBody = () => {
        if (!file) return null;
        if (kind === "unsupported") {
            return (
                <div
                    className="flex flex-col items-center justify-center gap-3 py-12 text-center text-sm text-slate-600"
                    data-testid="file-preview-unsupported"
                >
                    <FileWarning size={32} className="text-amber-600" />
                    <p>Preview not supported for this file type.</p>
                    <p className="text-xs text-slate-500">
                        {file.filename}
                        {file.mime_type ? ` · ${file.mime_type}` : ""}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={doDownload}
                        data-testid="file-preview-unsupported-download"
                        className="gap-1.5"
                    >
                        <Download size={14} /> Download
                    </Button>
                </div>
            );
        }
        if (previewError) {
            return (
                <div
                    className="flex flex-col items-center justify-center gap-3 py-12 text-center text-sm text-red-700"
                    data-testid="file-preview-error"
                >
                    {previewError}
                    <Button
                        type="button"
                        variant="outline"
                        onClick={doDownload}
                        data-testid="file-preview-error-download"
                        className="gap-1.5"
                    >
                        <Download size={14} /> Download instead
                    </Button>
                </div>
            );
        }
        if (previewLoading || !previewBlobUrl) {
            return (
                <div
                    className="py-12 text-center text-sm text-slate-500"
                    data-testid="file-preview-loading"
                >
                    Loading preview…
                </div>
            );
        }
        if (kind === "image") {
            return (
                <div className="flex justify-center bg-slate-50 rounded-md border border-slate-200 p-3">
                    <img
                        src={previewBlobUrl}
                        alt={file.filename || "Preview"}
                        data-testid="file-preview-image"
                        className="max-h-[80vh] object-contain"
                    />
                </div>
            );
        }
        // pdf
        return (
            <PdfPreview
                src={previewBlobUrl}
                downloadHref={previewBlobUrl}
                downloadName={file.filename || "document.pdf"}
                testid="file-preview-pdf"
                toolbar
            />
        );
    };

    const reasonTooLong = rejectReason.length > REJECT_REASON_MAX;
    const reasonEmpty = rejectReason.trim().length === 0;
    const rejectSubmitDisabled = busy || reasonEmpty || reasonTooLong;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="file-preview-modal"
                data-mode={mode}
                className="max-w-4xl"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        {file?.filename || "Document preview"}
                    </DialogTitle>
                    <DialogDescription>
                        {file?.mime_type || "—"}
                        {file?.size ? ` · ${(file.size / 1024).toFixed(0)} KB` : ""}
                        {status ? ` · ${status}` : ""}
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[70vh] overflow-y-auto">
                    {renderPreviewBody()}
                </div>

                {mode === "admin" && rejecting && (
                    <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="file-preview-reject-pane">
                        <Label htmlFor="upload-reject-reason">
                            Rejection reason <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                            id="upload-reject-reason"
                            data-testid="file-preview-reject-reason"
                            rows={3}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="e.g. Blurry scan; cannot read policy number."
                            maxLength={REJECT_REASON_MAX + 50}
                        />
                        <div
                            className={`text-[11px] text-right ${reasonTooLong ? "text-red-600 font-medium" : "text-slate-500"}`}
                            data-testid="file-preview-reject-counter"
                        >
                            {rejectReason.length} / {REJECT_REASON_MAX}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={doDownload}
                        data-testid="file-preview-download"
                        className="gap-1.5"
                    >
                        <Download size={14} /> Download
                    </Button>

                    {mode === "admin" && !rejecting && (
                        <>
                            <Button
                                type="button"
                                onClick={() => setRejecting(true)}
                                data-testid="file-preview-reject-open"
                                disabled={busy}
                                className="bg-red-600 hover:bg-red-700 gap-1.5"
                            >
                                <XCircle size={14} /> Reject
                            </Button>
                            <Button
                                type="button"
                                onClick={doApprove}
                                data-testid="file-preview-approve"
                                disabled={busy}
                                className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
                            >
                                <CheckCircle2 size={14} /> Approve
                            </Button>
                        </>
                    )}

                    {mode === "admin" && rejecting && (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => { setRejecting(false); setRejectReason(""); }}
                                disabled={busy}
                                data-testid="file-preview-reject-cancel"
                            >
                                Back
                            </Button>
                            <Button
                                type="button"
                                onClick={doReject}
                                disabled={rejectSubmitDisabled}
                                data-testid="file-preview-reject-submit"
                                className="bg-red-600 hover:bg-red-700 gap-1.5 disabled:opacity-60"
                            >
                                <XCircle size={14} /> Confirm reject
                            </Button>
                        </>
                    )}

                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange?.(false)}
                        data-testid="file-preview-close"
                    >
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default FilePreviewModal;
